"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useFirebaseAuth } from "@/lib/firebase-auth-context";
import {
  getCustomerProfile,
  saveCustomerProfile,
  type CustomerAddress,
  type CustomerProfile,
} from "@/lib/customer-profile";

/** Snipcart-shaped address — what the cart.update API accepts.
 *
 *  Snipcart's validator is strict (verified via a live probe of the
 *  staging cart, May 2026):
 *  - country MUST be a 2-letter ISO code (e.g. "GB") — sending "United
 *    Kingdom" returns errors.disabled_country.
 *  - BOTH `name` AND a `firstName`/`lastName` pair are required on every
 *    address — sending only `name` returns "The 'firstName' field is
 *    required."; sending only `firstName`/`lastName` returns "The 'name'
 *    field is required."
 *  - The server also rejects address updates on an empty cart with
 *    snipcart_error_operation_failed → "Received invalid payload from
 *    server" — addresses can only be set once at least one item is in
 *    the cart. The applyToCart code below guards on that. */
export type SnipcartAddress = {
  name?: string;
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  phone?: string;
};

function isCompleteAddress(value: SnipcartAddress | undefined): boolean {
  if (!value) return false;
  // The keys Snipcart actually validates as required against the structural
  // address shape. Province is optional (UK addresses often have no useful
  // county) and country gets a default below.
  return [
    "name",
    "address1",
    "city",
    "postalCode",
  ].every((key) =>
    typeof value[key as keyof SnipcartAddress] === "string" &&
    (value[key as keyof SnipcartAddress] as string).trim().length > 0,
  );
}

// Tiny country-name → ISO-2 map. Snipcart rejects long-form names
// (errors.disabled_country) so we have to translate. Add entries as we
// extend the storefront beyond the UK.
const COUNTRY_NAME_TO_ISO2: Record<string, string> = {
  "united kingdom": "GB",
  "uk": "GB",
  "great britain": "GB",
  "england": "GB",
  "scotland": "GB",
  "wales": "GB",
  "northern ireland": "GB",
  "republic of ireland": "IE",
  "ireland": "IE",
  "united states": "US",
  "united states of america": "US",
  "usa": "US",
};

function toIso2Country(value: string | undefined): string {
  const trimmed = value?.trim() ?? "";
  // Already a 2-letter ISO code — pass through.
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
  const mapped = COUNTRY_NAME_TO_ISO2[trimmed.toLowerCase()];
  if (mapped) return mapped;
  // Default to GB since the storefront only delivers in the UK today.
  // Better-than-nothing: an unrecognised country is more likely a missing
  // value than a genuine non-UK address.
  return "GB";
}

function splitFullName(fullName: string | undefined): {
  firstName: string;
  lastName: string;
} {
  const trimmed = fullName?.trim() ?? "";
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" ") || "",
  };
}

function withSnipcartDefaults(value: SnipcartAddress): SnipcartAddress {
  // Snipcart wants country as 2-letter ISO + BOTH the combined `name` AND
  // a separate `firstName`/`lastName` pair. We synthesise the missing
  // pieces from whatever we have rather than requiring callers to pre-split
  // them — the CustomerProfile / postcode-lookup paths only carry `name`.
  const out: SnipcartAddress = {
    ...value,
    country: toIso2Country(value.country),
    province: typeof value.province === "string" ? value.province : "",
  };
  if (!out.firstName && !out.lastName && out.name) {
    const { firstName, lastName } = splitFullName(out.name);
    out.firstName = firstName;
    out.lastName = lastName;
  } else if (!out.name && (out.firstName || out.lastName)) {
    out.name = `${out.firstName ?? ""} ${out.lastName ?? ""}`.trim();
  }
  return out;
}

/** Payload posted by snipcart.tsx when an order completes. */
type OrderProfileCapture = {
  email?: string | null;
  shippingAddress?: SnipcartAddress | null;
  billingAddress?: SnipcartAddress | null;
};

/**
 * The resolved customer "what should we prefill on the cart" state. Built
 * from Firebase Auth + the Firestore profile (signed-in web), or from URL
 * query params (the Flutter WebView path, which is not signed into web
 * Firebase but passes the same identity on the checkout URL).
 */
export type CustomerContext = {
  uid?: string;
  email?: string;
  phone?: string;
  shipping?: SnipcartAddress;
  billing?: SnipcartAddress;
};

type SnipcartCartUpdate = {
  email?: string;
  shippingAddress?: SnipcartAddress;
  billingAddress?: SnipcartAddress;
  shippingAddressSameAsBilling?: boolean;
  metadata?: Record<string, unknown>;
};

type SnipcartApi = {
  cart?: {
    update?: (data: SnipcartCartUpdate) => Promise<unknown>;
  };
};

type SnipcartEvents = {
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
};

type SnipcartStore = {
  getState?: () => {
    cart?: {
      metadata?: Record<string, unknown> | null;
      items?: { items?: Array<unknown> } | null;
    };
  };
};

function cartHasItems(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const store = (window as unknown as { Snipcart?: { store?: SnipcartStore } })
      .Snipcart?.store;
    const items = store?.getState?.()?.cart?.items?.items;
    return Array.isArray(items) && items.length > 0;
  } catch {
    return false;
  }
}

/**
 * Reads the current cart's metadata object so we can spread it underneath our
 * additions. Without this, every `cart.update({ metadata: ... })` REPLACES
 * the metadata object whole (Snipcart does not deep-merge), so an unrelated
 * writer (e.g. the reorder-manifest stamper in snipcart.tsx, or the legacy
 * mobile bridge) can wipe `firebaseUid` out of the cart — which breaks the
 * server-side order-link Function. Always cooperative, never destructive.
 */
function readCurrentCartMetadata(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  try {
    const store = (window as unknown as { Snipcart?: { store?: SnipcartStore } })
      .Snipcart?.store;
    const metadata = store?.getState?.()?.cart?.metadata;
    if (metadata && typeof metadata === "object" && !Array.isArray(metadata)) {
      return { ...(metadata as Record<string, unknown>) };
    }
  } catch {
    /* store not ready or shape changed — fall through to {} */
  }
  return {};
}

declare global {
  interface Window {
    /**
     * Legacy globals — still published for backward compat with the
     * `writeReorderManifest` code path in `snipcart.tsx`, which reads them
     * when stamping the reorder-manifest metadata. The *authoritative*
     * prefill path is the direct `Snipcart.api.cart.update` call below.
     */
    __rlFirebaseUid?: string;
    __rlFirebaseEmail?: string;
    __rlCustomerPhone?: string;
    __rlShippingAddress?: SnipcartAddress;
    __rlBillingAddress?: SnipcartAddress;
    __rlStampCustomerMetadata?: () => void;
    /**
     * Snipcart's order.completed handler calls this to persist the customer's
     * email and addresses to Firestore. Always set in the web bundle; absent
     * in flows where Firebase Auth isn't signed in (e.g. the Flutter WebView,
     * which uses AsoRockBridge.postMessage instead).
     */
    __rlCaptureOrderProfile?: (payload: OrderProfileCapture) => void;
    Snipcart?: {
      api?: SnipcartApi;
      events?: SnipcartEvents;
    };
  }
}

function toSnipcartAddress(value: CustomerAddress | null | undefined):
  | SnipcartAddress
  | undefined {
  if (!value) return undefined;
  const out: SnipcartAddress = {
    name: value.name?.trim() || undefined,
    address1: value.line1?.trim() || undefined,
    address2: value.line2?.trim() || undefined,
    city: value.city?.trim() || undefined,
    province: value.province?.trim() || undefined,
    country: value.country?.trim() || undefined,
    postalCode: value.postcode?.trim() || undefined,
    phone: value.phone?.trim() || undefined,
  };
  return Object.values(out).some((field) => Boolean(field)) ? out : undefined;
}

function fromSnipcartAddress(value: SnipcartAddress | null | undefined):
  | CustomerAddress
  | undefined {
  if (!value) return undefined;
  const out: CustomerAddress = {
    name: value.name?.trim() || undefined,
    line1: value.address1?.trim() || undefined,
    line2: value.address2?.trim() || undefined,
    city: value.city?.trim() || undefined,
    province: value.province?.trim() || undefined,
    country: value.country?.trim() || undefined,
    postcode: value.postalCode?.trim() || undefined,
    phone: value.phone?.trim() || undefined,
  };
  return Object.values(out).some((field) => Boolean(field)) ? out : undefined;
}

function readSnipcartAddressFromQuery(
  params: URLSearchParams,
  key: string,
): SnipcartAddress | undefined {
  const raw = params.get(key);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as SnipcartAddress;
    return toSnipcartAddress(fromSnipcartAddress(parsed) ?? null);
  } catch {
    return undefined;
  }
}

/**
 * Builds the Snipcart `cart.update` payload from a customer context. Returns
 * `null` when there's nothing useful to send (signed-out, no URL fallback).
 *
 * Always injects `phone` onto both addresses (creating empty `{phone}` shells
 * when no address is known) so Snipcart's contact-phone field gets prefilled
 * even on the customer's first-ever checkout.
 */
/**
 * Recursively drops keys whose value is `undefined`. Snipcart's `cart.update`
 * appears to silently reject the entire address object if it contains any
 * explicit-`undefined` fields — the safer wire-format is to omit the key.
 */
function stripUndefined<T>(value: T): T {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefined(item))
      .filter((item) => item !== undefined) as unknown as T;
  }
  const cleaned: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (val !== undefined) {
      cleaned[key] = stripUndefined(val);
    }
  }
  return cleaned as T;
}

function withPhone(
  base: SnipcartAddress | undefined,
  phone: string | undefined,
): SnipcartAddress | undefined {
  if (!base && !phone) return undefined;
  const result = base ?? {};
  if (phone && !result.phone) {
    return { ...result, phone };
  }
  return result;
}

/**
 * Resolves the checkout channel for the `source` order-metadata key. App
 * checkouts run on the `/app/checkout` Flutter-WebView route (and may carry a
 * `?source=app-webview` marker); everything else is the web storefront. Pure
 * function of the environment so every writer asserts the SAME value
 * independently — there is no spread-and-hope that a racing re-merge can drop.
 * There is deliberately no server-side default: a correct value or none.
 */
function resolveCheckoutSource(): "mobile-app" | "website" {
  if (typeof window === "undefined") return "website";
  const path = window.location.pathname || "";
  const search = window.location.search || "";
  if (path.startsWith("/app/checkout") || search.indexOf("source=app-webview") !== -1) {
    return "mobile-app";
  }
  return "website";
}

function buildCartUpdate(ctx: CustomerContext): SnipcartCartUpdate | null {
  if (!ctx.uid && !ctx.email) {
    return null;
  }
  const update: SnipcartCartUpdate = {};
  // Always spread the cart's current metadata UNDER our additions so we
  // never wipe pre-existing keys (siteCode, reorderManifestV1, source, etc.)
  // that other writers stamped.
  const baseMetadata = readCurrentCartMetadata();
  if (ctx.uid || ctx.email) {
    update.metadata = { ...baseMetadata };
    if (ctx.uid) update.metadata.firebaseUid = ctx.uid;
    if (ctx.email) update.metadata.firebaseEmail = ctx.email;
    // Stamp the checkout channel on every metadata-bearing update. This is the
    // reliable writer for SIGNED-IN web + app checkouts (metadata-only, so it
    // does not hit Snipcart's address-bearing 400). Guest carts get `source`
    // from snipcart.tsx (web) / the bridge (app) instead.
    update.metadata.source = resolveCheckoutSource();
  }
  if (ctx.email) update.email = ctx.email;
  // NOTE: We intentionally DO NOT send shippingAddress / billingAddress via
  // cart.update. Verified live (May 2026) against the staging cart: every
  // address-bearing cart.update returns 400 "snipcart_error_operation_failed"
  // from app.snipcart.com/api/cart/{id} regardless of payload shape, even
  // when the payload exactly matches what Snipcart's own checkout form
  // submits. Email + metadata both go through cleanly via cart.update;
  // address prefill is handled by direct DOM injection of Snipcart's
  // billing/shipping forms — see fillSnipcartAddressForms below.
  return stripUndefined(update);
}

// --- DOM-based address prefill -------------------------------------------
// Snipcart's checkout forms render into the document as plain HTML forms
// with stable IDs. We fill them by name/autocomplete attribute, using a
// React-style descriptor write so Vue's reactivity picks up the change.
// Same technique as public/snipcart-postcode-lookup.js.
//
// MIRRORED IN src/pages/app/checkout.tsx (the Flutter-WebView bridge).
// This React component IS mounted on /app/checkout (via _app.tsx's
// standalone branch); on that route it builds its customer context from the
// bridge's URL params instead of Firebase Auth. The bridge ALSO inlines a
// vanilla-JS copy of the helpers below — it can't call this compiled module
// from its injected <script> string — so BOTH the component and the bridge
// run on app-WebView checkouts. The duplicated helpers are
// `fillSnipcartForm`, `fillSnipcartAddressForms`,
// `queryFormField`, `setSnipcartFieldValue`, `withSnipcartDefaults`,
// `toIso2Country`, `splitFullName`, `withPhone`, plus the
// COUNTRY_NAME_TO_ISO2 + FIELD_AUTOCOMPLETE_FALLBACK constants — into
// `buildCheckoutBridgeScript`. If you change one, change BOTH.

type SnipcartFieldName =
  | "name"
  | "firstName"
  | "lastName"
  | "address1"
  | "address2"
  | "city"
  | "province"
  | "country"
  | "postalCode"
  | "phone";

const FIELD_AUTOCOMPLETE_FALLBACK: Record<SnipcartFieldName, string[]> = {
  name: ['input[autocomplete="name"]'],
  firstName: ['input[autocomplete="given-name"]'],
  lastName: ['input[autocomplete="family-name"]'],
  address1: [
    'input[autocomplete="address-line1"]',
    'textarea[autocomplete="street-address"]',
  ],
  address2: ['input[autocomplete="address-line2"]'],
  city: ['input[autocomplete="address-level2"]'],
  province: ['input[autocomplete="address-level1"]', 'select[autocomplete="address-level1"]'],
  country: ['select[autocomplete="country-name"]', 'input[autocomplete="country-name"]'],
  postalCode: ['input[autocomplete="postal-code"]'],
  phone: ['input[autocomplete="tel"]', 'input[type="tel"]'],
};

function queryFormField(
  form: HTMLFormElement,
  name: SnipcartFieldName,
): HTMLInputElement | HTMLSelectElement | null {
  // Snipcart uses `name="..."` on its inputs. Fall back to autocomplete-
  // attribute selectors, then to an id^="..." pattern (the field id is
  // suffixed with a random run-time hash).
  const directName = form.querySelector(
    `input[name="${name}"], select[name="${name}"], textarea[name="${name}"]`,
  );
  if (directName) return directName as HTMLInputElement | HTMLSelectElement;
  for (const selector of FIELD_AUTOCOMPLETE_FALLBACK[name]) {
    const fallback = form.querySelector(selector);
    if (fallback) return fallback as HTMLInputElement | HTMLSelectElement;
  }
  const idPrefix = form.querySelector(
    `input[id^="${name}_"], select[id^="${name}_"], textarea[id^="${name}_"]`,
  );
  return idPrefix as HTMLInputElement | HTMLSelectElement | null;
}

function setSnipcartFieldValue(
  field: HTMLInputElement | HTMLSelectElement,
  value: string,
) {
  if (field.tagName === "SELECT") {
    const select = field as HTMLSelectElement;
    const normalised = String(value || "")
      .trim()
      .toLowerCase();
    const match = Array.from(select.options).find(
      (option) =>
        option.value.trim().toLowerCase() === normalised ||
        option.textContent?.trim().toLowerCase() === normalised,
    );
    select.value = match ? match.value : value;
  } else {
    // Use the native property setter so React/Vue's getter/setter for the
    // value prop is bypassed and the value sticks.
    const descriptor = Object.getOwnPropertyDescriptor(
      Object.getPrototypeOf(field),
      "value",
    );
    if (descriptor?.set) {
      descriptor.set.call(field, value);
    } else {
      field.value = value;
    }
  }
  field.dispatchEvent(new Event("input", { bubbles: true }));
  field.dispatchEvent(new Event("change", { bubbles: true }));
  field.dispatchEvent(new Event("blur", { bubbles: true }));
}

function fillSnipcartForm(
  form: HTMLFormElement,
  address: SnipcartAddress,
  options: { fillEmail?: string; lockPhone?: boolean } = {},
) {
  const orderedFields: SnipcartFieldName[] = [
    "country",
    "firstName",
    "lastName",
    "name",
    "phone",
    "address1",
    "address2",
    "city",
    "province",
    "postalCode",
  ];
  for (const fieldName of orderedFields) {
    const value = address[fieldName];
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.length === 0) continue;
    const field = queryFormField(form, fieldName);
    if (!field) continue;
    setSnipcartFieldValue(field, value);
    if (fieldName === "phone" && options.lockPhone && field.tagName === "INPUT") {
      // Phone is the customer's auth identity when they're signed in via
      // Firebase phone-OTP. Locking it stops a customer from typing a
      // different number in checkout and ending up with the order linked
      // to the wrong (or no) account. The order-link Function's anti-spoof
      // is also loosened — but defence-in-depth: better to never tempt
      // the customer to edit it in the first place.
      field.setAttribute("readonly", "true");
      field.setAttribute(
        "title",
        "We're using the phone number from your account. Sign in with a different number to use that instead.",
      );
    }
  }
  if (options.fillEmail) {
    const emailField = form.querySelector(
      'input[type="email"], input[autocomplete="email"]',
    ) as HTMLInputElement | null;
    if (emailField && !emailField.value) {
      setSnipcartFieldValue(emailField, options.fillEmail);
    }
  }
}

function fillSnipcartAddressForms(ctx: CustomerContext, reason: string) {
  if (typeof document === "undefined") return false;
  const billingForm = document.querySelector<HTMLFormElement>(
    "form#snipcart-billing-form",
  );
  const shippingForm = document.querySelector<HTMLFormElement>(
    "form#snipcart-shipping-address-form",
  );
  if (!billingForm && !shippingForm) return false;
  // Build the Snipcart-shaped address (firstName/lastName synthesised, ISO
  // country, defaulted province). Phone is injected onto each address.
  const shipBase = withPhone(ctx.shipping, ctx.phone);
  const billBase = withPhone(ctx.billing, ctx.phone);
  const shipReady = shipBase ? withSnipcartDefaults(shipBase) : null;
  const billReady = billBase ? withSnipcartDefaults(billBase) : null;
  // Common case: only shipping is saved → billing mirrors shipping.
  const billFallback = billReady ?? shipReady;
  // Lock the phone field only when the customer is signed in (we have a
  // ctx.phone from Firebase Auth, not just from URL fallback). Anonymous
  // checkouts keep the phone field editable.
  const lockPhone = !!ctx.uid && !!ctx.phone;
  let filledBilling = false;
  let filledShipping = false;
  if (billingForm && billFallback) {
    fillSnipcartForm(billingForm, billFallback, {
      fillEmail: ctx.email,
      lockPhone,
    });
    filledBilling = true;
  }
  if (shippingForm && shipReady) {
    fillSnipcartForm(shippingForm, shipReady, { lockPhone });
    filledShipping = true;
  }
  if (filledBilling || filledShipping) {
    // eslint-disable-next-line no-console
    console.log("[rl] checkout form prefill", {
      reason,
      filledBilling,
      filledShipping,
    });
  }
  return filledBilling || filledShipping;
}

/**
 * Resolves when Snipcart's `cart.update` API is available, or false on
 * timeout. Polled because Snipcart loads asynchronously and the `ready`
 * event may have fired before this component mounts.
 */
async function waitForSnipcartReady(timeoutMs = 30_000): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (window.Snipcart?.api?.cart?.update) return true;
    await new Promise((resolve) => setTimeout(resolve, 200));
  }
  return false;
}

/**
 * Drives the Snipcart cart's customer fields (email, phone, billing,
 * shipping, metadata.firebaseUid) directly from React state.
 *
 * Why this owns it instead of `snipcart.tsx`: the previous design had
 * `SnipcartCustomerLink` publish window globals that `snipcart.tsx` would
 * consume opportunistically inside `writeReorderManifest`. That cooperation
 * raced on first item add: when the user's first `item.added` event fired
 * before this component had loaded the Firestore profile (or before
 * `__rlStampCustomerMetadata` was defined), the cart was stamped with no
 * `firebaseUid` — which silently dropped the order-link event AND the
 * checkout prefill in one go. Verified live via App Insights traces
 * showing the CMS never published a `CustomerOrderLinkedEvent` for any
 * test order despite the customer being signed in.
 *
 * This component is mounted once, app-wide. Renders nothing.
 */
export function SnipcartCustomerLink() {
  const { user } = useFirebaseAuth();
  const uid = user?.uid ?? null;
  // The loaded profile is keyed by the uid it belongs to; `profile` below is
  // derived from it so sign-out (or a different user signing in) never sees a
  // stale profile — without a synchronous setState in the effect.
  const [loadedProfile, setLoadedProfile] = useState<{
    uid: string;
    profile: CustomerProfile | null;
  } | null>(null);

  // Load the Firestore profile whenever the signed-in user changes.
  useEffect(() => {
    if (!user) {
      return;
    }
    const activeUid = user.uid;
    let active = true;
    void getCustomerProfile(activeUid)
      .then((loaded) => {
        if (active) setLoadedProfile({ uid: activeUid, profile: loaded });
      })
      .catch(() => {
        if (active) setLoadedProfile({ uid: activeUid, profile: null });
      });
    return () => {
      active = false;
    };
  }, [user]);

  const profile =
    user && loadedProfile?.uid === user.uid ? loadedProfile.profile : null;

  // Build the customer context. Signed-in web → Firebase + Firestore profile.
  // Signed-out → URL params (the Flutter WebView path).
  const context = useMemo<CustomerContext>(() => {
    if (typeof window === "undefined") return {};
    if (user) {
      return {
        uid: user.uid,
        email: profile?.email ?? user.email ?? undefined,
        phone: profile?.phone ?? user.phoneNumber ?? undefined,
        shipping: toSnipcartAddress(profile?.defaultShippingAddress),
        billing: toSnipcartAddress(profile?.defaultBillingAddress),
      };
    }
    const params = new URLSearchParams(window.location.search);
    const urlUid = params.get("customerUid")?.trim();
    const urlEmail = params.get("customerEmail")?.trim();
    const urlPhone = params.get("customerPhone")?.trim();
    return {
      uid: urlUid && urlUid.length > 0 ? urlUid : undefined,
      email: urlEmail && urlEmail.length > 0 ? urlEmail : undefined,
      phone: urlPhone && urlPhone.length > 0 ? urlPhone : undefined,
      shipping: readSnipcartAddressFromQuery(params, "shippingAddress"),
      billing: readSnipcartAddressFromQuery(params, "billingAddress"),
    };
  }, [user, profile]);

  // Publish the legacy window globals so `snipcart.tsx`'s
  // `writeReorderManifest` still has them for the reorder-manifest path.
  // The direct cart.update below is the *authoritative* prefill path.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__rlFirebaseUid = context.uid;
    window.__rlFirebaseEmail = context.email;
    window.__rlCustomerPhone = context.phone;
    window.__rlShippingAddress = context.shipping;
    window.__rlBillingAddress = context.billing;
    window.__rlStampCustomerMetadata?.();
  }, [context]);

  // Authoritative cart.update orchestration. Always runs once on mount,
  // re-runs on every context change, and re-applies on cart.opened so
  // a cart that Snipcart cleared between sessions gets re-stamped.
  const contextRef = useRef<CustomerContext>(context);
  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;

    async function applyToCart(reason: string) {
      const payload = buildCartUpdate(contextRef.current);
      if (!payload) return;
      const ready = await waitForSnipcartReady();
      if (cancelled || !ready) return;
      try {
        await window.Snipcart!.api!.cart!.update!(payload);
        // eslint-disable-next-line no-console
        console.log("[rl] cart.update applied", {
          reason,
          sent: Object.keys(payload),
          hasEmail: !!payload.email,
          hasFirebaseUid: !!(payload.metadata as Record<string, unknown> | undefined)?.firebaseUid,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[rl] cart.update failed", { reason, error });
      }
    }

    // Schedule a few attempts at filling Snipcart's billing/shipping forms.
    // Snipcart's Vue checkout renders async after `theme.routechanged`, so a
    // single pass right after the event sometimes misses fields. The
    // retries make the prefill robust without hammering the DOM.
    function scheduleAddressFill(reason: string) {
      const delays = [50, 250, 750, 1500];
      for (const delay of delays) {
        window.setTimeout(() => {
          if (cancelled) return;
          fillSnipcartAddressForms(contextRef.current, `${reason}+${delay}ms`);
        }, delay);
      }
    }

    // Initial apply.
    void applyToCart("mount");

    // Subscribe to Snipcart events once the events surface is ready.
    // Snipcart's events.on has no matching off; we accept that and capture
    // the latest context via contextRef. Handler is registered ONCE per
    // component lifetime (this effect has no deps).
    //
    // cart.update covers email + metadata.firebaseUid (the only fields
    // Snipcart's server accepts via the API path). Address prefill is
    // done by DOM-filling Snipcart's own checkout forms when the user
    // reaches the billing/shipping step — Snipcart's server rejects
    // address-bearing cart.update calls outright (verified May 2026).
    let interval: ReturnType<typeof setInterval> | undefined;
    let subscribed = false;
    function trySubscribe() {
      const events = window.Snipcart?.events;
      if (subscribed || !events?.on) return false;
      events.on("item.added", () => void applyToCart("item.added"));
      events.on("cart.opened", () => {
        void applyToCart("cart.opened");
        scheduleAddressFill("cart.opened");
      });
      events.on("cart.confirmed", () => void applyToCart("cart.confirmed"));
      // theme.routechanged fires every time the user moves between
      // Snipcart's checkout steps (cart → billing → shipping → payment).
      // Filling on this event covers the case where the user opens the
      // cart drawer and then proceeds — the billing/shipping forms only
      // exist in the DOM after the route change to those steps.
      events.on("theme.routechanged", () => scheduleAddressFill("route-changed"));
      // page.changed / billingaddress.changed / shippingaddress.changed
      // are additional belt-and-braces signals that Snipcart's docs
      // mention but don't formally guarantee — re-fill on any of them.
      events.on("page.changed", () => scheduleAddressFill("page.changed"));
      subscribed = true;
      return true;
    }
    if (!trySubscribe()) {
      interval = setInterval(() => {
        if (cancelled || trySubscribe()) {
          if (interval !== undefined) {
            clearInterval(interval);
            interval = undefined;
          }
        }
      }, 250);
    }

    return () => {
      cancelled = true;
      if (interval !== undefined) clearInterval(interval);
    };
  }, []);

  // Re-apply whenever the context (sign-in state or profile) changes.
  // Important for the case where a user adds items BEFORE signing in:
  // item.added has already fired, so the only way to stamp the cart with
  // their email/firebaseUid once they sign in is on this context update.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    void (async () => {
      const payload = buildCartUpdate(context);
      if (!payload) return;
      const ready = await waitForSnipcartReady();
      if (cancelled || !ready) return;
      try {
        await window.Snipcart!.api!.cart!.update!(payload);
        // eslint-disable-next-line no-console
        console.log("[rl] cart.update applied", {
          reason: "context-change",
          sent: Object.keys(payload),
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[rl] cart.update failed", {
          reason: "context-change",
          error,
        });
      }
      // If the customer is now on the billing/shipping step, refresh the
      // address forms with their newly-loaded profile.
      fillSnipcartAddressForms(context, "context-change");
    })();
    return () => {
      cancelled = true;
    };
  }, [context]);

  // Install the Firestore writer that snipcart.tsx's order.completed handler
  // invokes to persist captured email + addresses back to the profile.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!uid) {
      delete window.__rlCaptureOrderProfile;
      return;
    }
    window.__rlCaptureOrderProfile = (payload) => {
      const update: Parameters<typeof saveCustomerProfile>[1] = {};
      // Only ever write fields we actually captured. We used to set
      // email/billing/shipping to null when the extractor returned nothing,
      // which silently wiped values the customer had saved manually on
      // /account/contact-details. Treat absence as "leave alone".
      const trimmedEmail = payload.email?.trim();
      if (trimmedEmail) {
        update.email = trimmedEmail;
      }
      const shipping = fromSnipcartAddress(payload.shippingAddress ?? null);
      if (shipping) {
        update.defaultShippingAddress = shipping;
      }
      const billing = fromSnipcartAddress(payload.billingAddress ?? null);
      if (billing) {
        update.defaultBillingAddress = billing;
      }
      if (Object.keys(update).length === 0) return;
      void saveCustomerProfile(uid, update).catch((error) => {
        // eslint-disable-next-line no-console
        console.error(
          "[rl] order.completed → saveCustomerProfile failed:",
          error,
        );
      });
    };
    return () => {
      delete window.__rlCaptureOrderProfile;
    };
  }, [uid]);

  return null;
}
